/**
 * 비즈니스 통계 데모 페이지
 * 간단한 차트와 통계로 요약 표시
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChartBarIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import DemoNavigation from '../components/DemoNavigation';

const DemoBusinessPage: React.FC = () => {
  const navigate = useNavigate();

  // Mock 통계 데이터
  const stats = {
    monthlyRevenue: 12500000,
    totalMembers: 85,
    activeMembers: 72,
    averageUsageTime: 6.5,
    occupancyRate: 78,
  };

  // Mock 월별 매출 데이터
  const monthlyData = [
    { month: '7월', revenue: 9800000 },
    { month: '8월', revenue: 10200000 },
    { month: '9월', revenue: 11500000 },
    { month: '10월', revenue: 10800000 },
    { month: '11월', revenue: 11800000 },
    { month: '12월', revenue: 12500000 },
  ];

  // Mock 회원 유형별 분포
  const membershipTypes = [
    { type: '정기권', count: 45, percentage: 53 },
    { type: '시간권', count: 28, percentage: 33 },
    { type: '자유석', count: 12, percentage: 14 },
  ];

  const maxRevenue = Math.max(...monthlyData.map((d) => d.revenue));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 데모 헤더 */}
      <div className="bg-blue-600 text-white py-3 px-4 text-center">
        <p className="text-sm">
          📢 이것은 데모 페이지입니다. 모든 데이터는 가상 데이터이며, 데모 시연을 위해 주기적으로 업데이트되어 조금씩 변동될 수 있습니다.
        </p>
      </div>

      {/* 네비게이션 헤더 */}
      <DemoNavigation currentTitle="비즈니스 통계 (데모)" currentIcon={ChartBarIcon} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 안내 메시지 */}
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            💡 매출, 회원, 이용률 등 주요 비즈니스 지표를 확인할 수 있습니다!
          </p>
        </div>

        {/* 주요 지표 카드 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="text-sm opacity-90">이번 달 매출</p>
                <p className="text-3xl font-bold mt-2">
                  ₩{(stats.monthlyRevenue / 10000).toFixed(0)}만
                </p>
              </div>
              <CurrencyDollarIcon className="h-12 w-12 opacity-80" />
            </div>
            <div className="flex items-center gap-1 text-sm">
              <ArrowTrendingUpIcon className="h-4 w-4" />
              <span>전월 대비 +8.5%</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="text-sm opacity-90">전체 회원</p>
                <p className="text-3xl font-bold mt-2">{stats.totalMembers}명</p>
              </div>
              <UserGroupIcon className="h-12 w-12 opacity-80" />
            </div>
            <div className="flex items-center gap-1 text-sm">
              <span>활성 회원: {stats.activeMembers}명</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="text-sm opacity-90">좌석 이용률</p>
                <p className="text-3xl font-bold mt-2">{stats.occupancyRate}%</p>
              </div>
              <ChartBarIcon className="h-12 w-12 opacity-80" />
            </div>
            <div className="flex items-center gap-1 text-sm">
              <span>평균 재실 시간: {stats.averageUsageTime}h</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="text-sm opacity-90">평균 이용 시간</p>
                <p className="text-3xl font-bold mt-2">{stats.averageUsageTime}h</p>
              </div>
              <ClockIcon className="h-12 w-12 opacity-80" />
            </div>
            <div className="flex items-center gap-1 text-sm">
              <span>일 평균 기준</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 월별 매출 추이 */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-6">월별 매출 추이</h2>
            <div className="space-y-4">
              {monthlyData.map((data, index) => (
                <div key={index}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">{data.month}</span>
                    <span className="text-sm font-bold text-gray-900">
                      ₩{(data.revenue / 10000).toFixed(0)}만
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${(data.revenue / maxRevenue) * 100}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-900">
                💰 최근 6개월간 꾸준한 성장세를 보이고 있습니다!
              </p>
            </div>
          </div>

          {/* 회원 유형별 분포 */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-6">회원 유형별 분포</h2>
            <div className="space-y-6">
              {membershipTypes.map((type, index) => (
                <div key={index}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">{type.type}</span>
                    <span className="text-sm font-bold text-gray-900">{type.count}명</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          index === 0
                            ? 'bg-blue-500'
                            : index === 1
                            ? 'bg-green-500'
                            : 'bg-purple-500'
                        }`}
                        style={{ width: `${type.percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-semibold text-gray-600">
                      {type.percentage}%
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">주요 인사이트</h3>
              <ul className="space-y-2 text-xs text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-blue-500">•</span>
                  <span>정기권 회원이 가장 많음 (53%)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500">•</span>
                  <span>시간권 수요도 높은 편 (33%)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500">•</span>
                  <span>자유석 이용자 꾸준히 증가 중</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* 추가 분석 섹션 */}
        <div className="mt-6 bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">운영 현황 요약</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">최다 이용 시간대</p>
              <p className="text-2xl font-bold text-blue-600">18:00 - 22:00</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">평균 체류 시간</p>
              <p className="text-2xl font-bold text-green-600">6.5시간</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">신규 가입 (이번 달)</p>
              <p className="text-2xl font-bold text-purple-600">12명</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoBusinessPage;
